'use client';

import { useTranslation } from '@/contexts/dictionaryContext';
import { IDatePickerDictonary } from '@/dictionaries/components/datePicker/types';
import { IModalsDictionary } from '@/dictionaries/components/modals/types';
import { IQuantitiesInputDictonary } from '@/dictionaries/components/quantitiesInput/types';
import IVillageSideColumnDictionary from '@/dictionaries/components/villageSideColumn/types';
import { IContactPageFormDictionary } from '@/dictionaries/pages/contacts/types';
import { useLocale } from '@/hooks/useLocale';
import useUserContext from '@/hooks/useUserContext';
import {
  Experience,
  ExperienceTranslation,
  VillaBooking,
} from '@/services/experienceTypes';
import { experienceNeedLogin } from '@/utils/experienceNeedLogin';
import parser from 'html-react-parser';
import { useEffect, useState } from 'react';
import { GoDash, GoPlus } from 'react-icons/go';
import { RequestCard } from './RequestCard';
import styles from './styles.module.css';

interface IExperienceDetailsProps {
  translation: ExperienceTranslation;
  experience: Experience;
  villaBooking?: VillaBooking[];
  datePickerDict: IDatePickerDictonary;
  quantitiesInputDict: IQuantitiesInputDictonary;
  villageSideColumnDict: IVillageSideColumnDictionary;
  formDict: IContactPageFormDictionary;
  modalsDict: IModalsDictionary;
}

export default function ExperienceDetails({
  translation,
  experience,
  villaBooking,
  datePickerDict,
  quantitiesInputDict,
  villageSideColumnDict,
  formDict,
  modalsDict,
}: IExperienceDetailsProps) {
  const enabledOptions = experience.options.filter((option) => option.enabled);

  const { experienceInfo: dict } = useTranslation();
  const lang = useLocale();

  const [showDescription, setShowDescription] = useState<boolean[]>(
    enabledOptions.map(() => false),
  );

  const { isLogged } = useUserContext();

  const needLogin = experienceNeedLogin(isLogged, experience);

  const optionsTranslation = enabledOptions.map((item) => {
    const translations = item.translations.find((option) => {
      if (option.locale === lang) {
        return option;
      }
    });

    return {
      id: item.id.toString(),
      ...translations,
    };
  });

  const toggleDescription = (index: number) => {
    const newShowDescription = [...showDescription];
    newShowDescription[index] = !newShowDescription[index];
    setShowDescription(newShowDescription);
  };

  useEffect(() => {
    const slugs = experience.experienceTranslation.reduce(
      (acc: { [key: string]: string }, cur) => {
        acc[cur.locale] = cur.slug;
        return acc;
      },
      {},
    );

    localStorage.setItem('expSlugs', JSON.stringify(slugs));
    // eslint-disable-next-line
  }, []);

  const options = optionsTranslation.map((item, index) => {
    return (
      <div
        key={index}
        className={`${
          index + 1 !== enabledOptions.length && 'border-b-2 border-gray-200'
        } py-[20px]`}>
        <div className="flex flex-col items-start">
          <span
            className="flex items-center min-w-[30%] gap-2 text-subtitle font-averta-semibold cursor-pointer"
            onClick={() => toggleDescription(index)}>
            {showDescription[index] ? (
              <GoDash className="text-blue w-[20px]" />
            ) : (
              <GoPlus className="text-blue" />
            )}
            {item?.title}
          </span>
          <div className={showDescription[index] ? styles.show : styles.hide}>
            {showDescription && <p>{item?.description}</p>}
          </div>
        </div>
      </div>
    );
  });

  return (
    <div className="flex mb-[30px] sm:mb-[50px] desktop:mb-20 px-sm md:px-md desktop:px-lg w-full max-w-default desktop:mx-auto">
      <RequestCard
        lang={lang}
        dict={dict}
        datePickerDict={datePickerDict}
        quantitiesInputDict={quantitiesInputDict}
        villageSideColumnDict={villageSideColumnDict}
        formDict={formDict}
        modalsDict={modalsDict}
        title={translation.title}
        experience={experience}
        experienceOptions={enabledOptions}
        optionsTranslation={optionsTranslation}
        media={experience.media}
        villaBooking={villaBooking}
        needLogin={needLogin}
      />
      <div className="desktop:w-[calc(100% - 490px + 5rem)] desktop:pl-[30px]">
        <div>
          <div id="the-experience" className="mb-5">
            <h2 className="text-4xl mb-8 font-noe">
              {dict.details.theExperience}
            </h2>
            {translation.description.includes('<p>') ? (
              parser(translation.description)
            ) : (
              <p>{translation.description}</p>
            )}
          </div>
          <h3 className="text-subtitle mb-1 font-averta-semibold">
            {dict.details.greatFor}
          </h3>
          <p className="mb-5">{translation.greatForDescription}</p>
          <h3 className="text-subtitle mb-1 font-averta-semibold">
            {dict.details.availability}
          </h3>
          <p className="mb-5" style={{ whiteSpace: 'pre-line' }}>
            {translation.availabilityDescription}
          </p>
          <h3 className="text-subtitle mb-1 font-averta-semibold">
            {dict.details.included}
          </h3>
          <p className="mb-5" style={{ whiteSpace: 'pre-line' }}>
            {translation.includedDescription}
          </p>
          <h3 className="text-subtitle mb-1 font-averta-semibold">
            {dict.details.notIncluded}
          </h3>
          <p className="mb-5" style={{ whiteSpace: 'pre-line' }}>
            {translation.notIncludedDescription}
          </p>
          <h3 className="text-subtitle mb-1 font-averta-semibold">
            {dict.details.needToKnow}
          </h3>
          <p className="mb-10" style={{ whiteSpace: 'pre-line' }}>
            {translation.needToKnowDescription}
          </p>
        </div>
        {optionsTranslation[0]?.description && (
          <div id="options">
            <h2 className="text-4xl mb-4 font-noe">{dict.details.options}</h2>
            <div className="mb-5">{options}</div>
          </div>
        )}
        {(!experience.inVilla || isLogged) && (
          <div id="conditions">
            <h2 className="text-4xl mb-8 font-noe">
              {dict.details.conditions}
            </h2>
            <h3 className="text-subtitle mb-1 font-averta-semibold">
              {dict.details.payment}
            </h3>
            <p className="mb-5">{translation.paymentConditions}</p>
            <h3 className="text-subtitle mb-1 font-averta-semibold">
              {dict.details.cancellation}
            </h3>
            <p className="mb-5" style={{ whiteSpace: 'pre-line' }}>
              {translation.cancellationConditions}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
